import { CreateSupporterMemberBody, CreateSupporterMembersBody } from "@givenwell/management-api";

/**
 * The AWS WAF in front of the API only inspects — and rejects — request bodies larger than 8KB, so
 * bulk member creation has to be split into several requests. We aim well below 8KB to leave room
 * for any body fields the API client adds and for multi-byte characters in names/email addresses.
 */
export const MAX_BULK_MEMBERS_BODY_BYTES = 6 * 1024;

const encoder = new TextEncoder();

function encodedSize(value: unknown) {
  return encoder.encode(JSON.stringify(value)).length;
}

/**
 * Splits `members` into the fewest chunks whose serialised request bodies each stay under
 * `maxBytes`. A single member larger than the budget gets its own chunk — we can't split it any
 * further, so it's better to send it and let the API respond than to silently drop it.
 */
export function chunkMembersForRequest(
  members: CreateSupporterMemberBody[],
  rest: Omit<CreateSupporterMembersBody, "members">,
  maxBytes = MAX_BULK_MEMBERS_BODY_BYTES,
): CreateSupporterMemberBody[][] {
  // Everything in the body except the members themselves: `{"email_timing":"...","members":[]}`.
  const overhead = encodedSize({ ...rest, members: [] });

  const chunks: CreateSupporterMemberBody[][] = [];
  let chunk: CreateSupporterMemberBody[] = [];
  let chunkSize = overhead;

  for (const member of members) {
    // +1 for the comma joining this member to the previous one in the array.
    const memberSize = encodedSize(member) + 1;

    if (chunk.length > 0 && chunkSize + memberSize > maxBytes) {
      chunks.push(chunk);
      chunk = [];
      chunkSize = overhead;
    }

    chunk.push(member);
    chunkSize += memberSize;
  }

  if (chunk.length > 0) chunks.push(chunk);

  return chunks;
}

/**
 * Thrown when a bulk create fails partway through: some chunks were already committed by the API,
 * so the caller needs to know which members exist now and must not be sent again.
 */
export class PartialBulkCreateError extends Error {
  constructor(
    readonly error: unknown,
    readonly createdEmails: string[],
  ) {
    super("Some members were created before the request failed");
    this.name = "PartialBulkCreateError";
  }
}
